import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Player from '~/Share/containers/Player';
import SideShare from '~/Share/components/Share/SideShare';
import { ReduxState } from '~/types/redux/share';
import { isVideoWatched } from '~/utils/video';
import { SquareVideoContainer } from './SquareVideoContainer';
import cx from 'classnames';
import { useMedia } from 'react-use-media';

export type VideoProps = {
  videoUrl: string;
  slug?: string;
  useDefaultPlayer?: boolean;
  showSocialShareBar?: boolean;
  showSingleColumnLayout?: boolean;
};

export default function Video({
  slug,
  videoUrl,
  useDefaultPlayer,
  showSocialShareBar,
  showSingleColumnLayout,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isWatched, setIsWatched] = useState(false);
  const {
    videoWatched,
    isRecipient,
    thumbUrl,
    transcript,
    messageTemplate,
    features,
  } = useSelector((state: ReduxState) => ({
    videoWatched: state.tracking.videoWatched,
    isRecipient: state.root.isRecipient,
    thumbUrl: state.root.model.thumb_url,
    transcript: state.root.model?.transcript,
    messageTemplate: state.root.messageTemplate,
    features: state.root.features,
  }));

  const { shouldHideBonjoroBranding } = features;
  const isMobile = useMedia({
    maxWidth: 850,
  });

  useEffect(() => {
    if (!useDefaultPlayer) return;

    const intervalId = setInterval(() => {
      if (!videoRef.current || isWatched) return;

      if (isVideoWatched(videoRef.current)) {
        videoWatched();
        setIsWatched(true);
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, [useDefaultPlayer, isWatched]);

  const videoPlayerProps = {
    videoUrl,
    thumbUrl,
    showReactionButton: isRecipient,
    transcript,
    videoPlayerContainerClassname:
      'w-full h-full rounded-none md:rounded-[10px]',
    primaryColor: messageTemplate?.primary_color,
    showBonjoroBadge: isMobile && !shouldHideBonjoroBranding,
  };

  return (
    <div className='box-video greet-video'>
      {useDefaultPlayer ? (
        <Player
          {...videoPlayerProps}
          videoClassName={cx({
            'max-w-[700px] max-h-[550px] !w-auto !h-auto':
              !showSingleColumnLayout,
          })}
        />
      ) : (
        <SquareVideoContainer>
          <Player {...videoPlayerProps} />
        </SquareVideoContainer>
      )}
      {showSocialShareBar && <SideShare slug={slug} />}
    </div>
  );
}
